/**
 * @fileoverview Project a BMaps Compound into the engine's typed-parameter
 * shape (flat Float64Arrays + Int32Arrays).
 *
 * This is the boundary between the BMaps OO model (Atom/Bond/Compound,
 * Angstrom units) and the engine's flat-array form (nm units, atomNames-
 * indexed). The engine never sees BMaps domain objects directly; this
 * function does the projection once.
 *
 * The atom index space throughout the engine is the position in the
 * residue's `atomNames` array (the same index space the server uses in
 * the v6 trailer's bonded/exclusion arrays).
 */

import { doWithActiveSolute } from '../../../server/activeSolute';
import { buildExclusionSets, normalizePackedPairs } from '../topology/exclusions.js';
import { SCEE, SCNB } from '../forces/nonbonded.js';

/** Angstroms -> nm */
const A_TO_NM = 0.1;

/**
 * @typedef {import('../types.js').LigandTopology} LigandTopology
 * @typedef {import('../types.js').LigandParameters} LigandParameters
 * @typedef {import('../../../model/AtomInfoMap.js').RawForcefieldData} RawForcefieldData
 * @typedef {import('../../../model/compound.js').Compound} Compound
 * @typedef {import('../../../DataConnection.js').default} DataConnection
 */

/**
 * @typedef {Object} GetTypedParametersOptions
 * @property {string} [forcefield]   Force field hint passed through to the
 *                                   server request ("Amber" or "OpenFF").
 *                                   Falls back to whatever the server defaults to.
 */

/**
 * Public entry point. Returns engine-ready typed parameters + topology +
 * starting coordinates for a compound. Handles the server lazy-fetch path
 * when `rawForcefieldData` is not yet in the cache.
 *
 * The connector and atomInfoManager are passed in (rather than imported
 * from a global) to keep this module testable and free of side-effect
 * imports. The call site (`localMinimize`) wires them.
 *
 * @param {Compound}         compound          BMaps Compound (proto/src/model/compound.js).
 * @param {import('BMapsSrc/model/AtomInfoManager.js').default} atomInfoManager
 *                                             AtomInfoManager from the active
 *                                             CaseDataCollection.
 * @param {DataConnection}   connector         Server connector (for
 *                                             cmdGetForcefieldParamsForLigand).
 * @param {GetTypedParametersOptions} [opts]
 * @returns {Promise<{
 *   topology: LigandTopology,
 *   parameters: LigandParameters,
 *   coords: Float64Array
 * }>}
 *
 * Throws if the server response does not include a v6 trailer (e.g.,
 * old server build). The caller (localMinimize) should surface this as
 * `EnergyInfo.failure`.
 */
export async function getTypedParameters(compound, atomInfoManager, connector, opts = {}) {
    const resname = compound.resname;

    // Lazy-fetch if no raw forcefield data in cache.
    let rawFFD = atomInfoManager.getLigandRawForcefieldData(resname);
    // Guard against a cached trailer from a different force field than requested.
    // Raw data can be populated by the server's unsolicited piggyback (sent on
    // load/edit using the server's session force field) as well as by an explicit
    // fetch; if the user switched the Forcefield preference and a stale-FF entry
    // survived (e.g. an edit re-piggybacked under the old FF before any re-fetch),
    // treat it as a miss and re-fetch with the requested FF. forcefieldVersion is
    // "gaff-*"/"parm*" for Amber, "openff-*" for OpenFF.
    if (rawFFD && opts.forcefield) {
        const cachedIsOpenFF = /openff/i.test(rawFFD.forcefieldVersion || '');
        const wantOpenFF = opts.forcefield === 'OpenFF';
        if (cachedIsOpenFF !== wantOpenFF) rawFFD = null;
    }
    if (!rawFFD) {
        // ligand_spec is resolved against the server's active solute, so the
        // repoint has to ride in the same turn as this send (BP2-2663).
        await doWithActiveSolute(compound, () => connector.cmdGetForcefieldParamsForLigand({
            ligand_spec: compound.resSpec,
            forcefield: opts.forcefield,
        }));
        rawFFD = atomInfoManager.getLigandRawForcefieldData(resname);
        if (!rawFFD) {
            throw new Error(
                `Server did not deliver v6 forcefield data for ligand ${resname}. `
                + 'This likely means the bfd-server build does not yet support the '
                + 'v6 get-forcefield-params response schema. Use MinimizationEngine = '
                + '"MM" or "BFD" via preferences.'
            );
        }
    }

    const atomInfo = atomInfoManager.getLigandAtomInfo(resname);
    if (!atomInfo) {
        throw new Error(`AtomInfoMap entry for ${resname} missing after forcefield fetch.`);
    }
    const { atomNames } = atomInfo;
    if (!atomNames || atomNames.length === 0) {
        throw new Error(`AtomInfoMap entry for ${resname} has no atomNames.`);
    }

    const nAtoms = atomNames.length;

    // Per-atom arrays from the raw v6 data (server-supplied, atomNames-indexed).
    // The v6 trailer ships float64 (for precision/force agreement); engine
    // native is f64.
    const sigma = new Float64Array(rawFFD.sigma);
    const epsilon = new Float64Array(rawFFD.epsilon);

    // Charges live in AtomInfoMap (per-atom, set by the v5 atom-info path).
    if (!atomInfo.charges || atomInfo.charges.length !== nAtoms) {
        throw new Error(
            `Charges missing or wrong-length for ${resname} `
            + `(have ${atomInfo.charges?.length ?? 0}, need ${nAtoms}).`
        );
    }
    const charges = Float64Array.from(atomInfo.charges);

    // Sanity: sigma/epsilon arrays from server must match atomNames length.
    if (sigma.length !== nAtoms || epsilon.length !== nAtoms) {
        throw new Error(
            `v6 sigma/epsilon array lengths (${sigma.length}/${epsilon.length}) `
            + `disagree with atomNames length (${nAtoms}) for ${resname}.`
        );
    }

    // Bonded terms: project arrays-of-objects into parallel typed arrays.
    const nBonds = rawFFD.bonds.length;
    const bondIdx = new Int32Array(nBonds * 2);
    const bondK = new Float64Array(nBonds);
    const bondR0 = new Float64Array(nBonds);
    for (let b = 0; b < nBonds; b++) {
        const term = rawFFD.bonds[b];
        bondIdx[2 * b] = term.i;
        bondIdx[2 * b + 1] = term.j;
        bondK[b] = term.k;
        bondR0[b] = term.r0;
    }

    const nAngles = rawFFD.angles.length;
    const angleIdx = new Int32Array(nAngles * 3);
    const angleK = new Float64Array(nAngles);
    const angleTheta0 = new Float64Array(nAngles);
    for (let a = 0; a < nAngles; a++) {
        const term = rawFFD.angles[a];
        angleIdx[3 * a] = term.i;
        angleIdx[3 * a + 1] = term.j;
        angleIdx[3 * a + 2] = term.k;
        angleK[a] = term.kForce;
        angleTheta0[a] = term.theta0;
    }

    // Torsions: AMBER cosine series allows multiple terms per (i,j,k,l) quad.
    // The engine wants per-quad torsions with a parallel "terms per torsion"
    // count array, then flat term-level k/n/phase arrays. Group sequential
    // entries that share the same atom quad. (If the server emits them
    // unsorted, this grouping will treat them as separate torsions — that
    // still produces correct energies, just with more iteration overhead.)
    const {
        torsionIdx, torsionTermsPerTorsion, torsionK, torsionN, torsionPhase,
    } = packTorsionSeries(rawFFD.torsions);
    const {
        torsionIdx: improperIdx,
        torsionTermsPerTorsion: improperTermsPerTorsion,
        torsionK: improperK,
        torsionN: improperN,
        torsionPhase: improperPhase,
    } = packTorsionSeries(rawFFD.impropers);

    // Topology: flat-pair / flat-triple / flat-quad index arrays. Proper and
    // improper torsions are combined here because the engine treats them
    // identically (same cosine-series functional form). The original
    // per-term separation is preserved in LigandParameters via the
    // termsPerTorsion arrays.
    const combinedTorsionIdx = concatInt32(torsionIdx, improperIdx);

    /** @type {LigandTopology} */
    const topology = {
        nAtoms,
        bonds: bondIdx,
        angles: angleIdx,
        torsions: combinedTorsionIdx,
    };

    // 1-4 scaling guard (fail closed). The nonbonded kernels — both the JS
    // forces/nonbonded.js and its WASM port — apply the 1-4 scaling as fixed
    // constants (SCEE/SCNB, the AMBER default divisors 1.2 / 2.0); they do not
    // read the per-ligand scee/scnb. Every force field BMaps emits (GAFF/parm,
    // OpenFF Sage) uses 1.2/2.0, and the bfd-server emitter already fails closed
    // on any non-uniform scaling — but if a trailer ever carries different
    // divisors, reject here (caller surfaces EnergyInfo.failure / server fallback)
    // rather than silently mis-scaling every 1-4 pair.
    const SCALE_TOL = 1e-3;
    if (Number.isFinite(rawFFD.scee) && Math.abs(rawFFD.scee - SCEE) > SCALE_TOL) {
        throw new Error(
            `v6 forcefield for ${resname} uses scee=${rawFFD.scee} (expected ${SCEE}); `
            + 'the in-browser engine applies a fixed AMBER 1-4 electrostatic scaling and '
            + 'cannot represent this force field.'
        );
    }
    if (Number.isFinite(rawFFD.scnb) && Math.abs(rawFFD.scnb - SCNB) > SCALE_TOL) {
        throw new Error(
            `v6 forcefield for ${resname} uses scnb=${rawFFD.scnb} (expected ${SCNB}); `
            + 'the in-browser engine applies a fixed AMBER 1-4 vdW scaling and '
            + 'cannot represent this force field.'
        );
    }

    /** @type {LigandParameters} */
    const parameters = {
        forcefieldVersion: rawFFD.forcefieldVersion,
        charges,
        sigma,
        epsilon,
        scee: rawFFD.scee,
        scnb: rawFFD.scnb,
        bondK,
        bondR0,
        angleK,
        angleTheta0,
        torsionTermsPerTorsion: concatInt32(torsionTermsPerTorsion, improperTermsPerTorsion),
        torsionK: concatFloat64(torsionK, improperK),
        torsionN: concatInt32(torsionN, improperN),
        torsionPhase: concatFloat64(torsionPhase, improperPhase),
        // Server-supplied, but normalized on ingest: the nonbonded sentinel-pointer
        // walk requires each pair stored with i<j and the list sorted ascending by
        // the packed (i<<16)|j key. A pair delivered as (j,i) with j>i in wire order
        // would never be matched by the walk, silently leaking that 1-2/1-3 pair
        // into the nonbonded sum. normalizePackedPairs canonicalizes i<j and sorts
        // (the same pipeline buildExclusionSets uses for the scaledPairs fallback).
        exclusions: normalizePackedPairs(rawFFD.exclusions),
        scaledPairs: deriveScaledPairs(rawFFD, nAtoms, bondIdx), // see below
    };

    // Starting coordinates: project compound atoms (Angstrom) into a flat
    // Float64Array (nm). Atoms are placed at slots 3*atomIdx + {0,1,2} where
    // atomIdx is the position of the atom's `atom` name in `atomNames`.
    const coords = new Float64Array(nAtoms * 3);
    const filled = new Uint8Array(nAtoms);
    for (const atom of compound.getAtoms()) {
        const idx = atomNames.indexOf(atom.atom);
        if (idx < 0) {
            console.warn(`Atom ${atom.atom} not in atomNames for ${resname}; skipped.`);
            continue;
        }
        coords[3 * idx] = atom.getX() * A_TO_NM;
        coords[3 * idx + 1] = atom.getY() * A_TO_NM;
        coords[3 * idx + 2] = atom.getZ() * A_TO_NM;
        filled[idx] = 1;
    }
    for (let i = 0; i < nAtoms; i++) {
        if (!filled[i]) {
            throw new Error(
                `Atom ${atomNames[i]} (index ${i}) of ${resname} not found in compound; `
                + 'cannot construct starting coordinates.'
            );
        }
    }

    return { topology, parameters, coords };
}

/**
 * Group sequential entries sharing the same (i,j,k,l) quad into a single
 * "torsion" with multiple cosine terms. Output structure matches what the
 * engine kernel wants: per-quad index + per-quad termCount + flat per-term
 * k/n/phase arrays.
 *
 * @param {Array<{
 *   i:number, j:number, k:number, l:number,
 *   periodicity:number, phase:number, kForce:number
 * }>} entries
 * @returns {{
 *   torsionIdx: Int32Array,
 *   torsionTermsPerTorsion: Int32Array,
 *   torsionK: Float64Array,
 *   torsionN: Int32Array,
 *   torsionPhase: Float64Array,
 * }}
 */
function packTorsionSeries(entries) {
    if (entries.length === 0) {
        return {
            torsionIdx: new Int32Array(0),
            torsionTermsPerTorsion: new Int32Array(0),
            torsionK: new Float64Array(0),
            torsionN: new Int32Array(0),
            torsionPhase: new Float64Array(0),
        };
    }
    // First pass: group consecutive entries with matching quads.
    const groups = []; // each: { i, j, k, l, terms: [{n, phase, k}, ...] }
    let cur = null;
    const sameQuad = (a, b) => a.i === b.i && a.j === b.j && a.k === b.k && a.l === b.l;
    for (const e of entries) {
        if (cur && sameQuad(cur, e)) {
            cur.terms.push({ n: e.periodicity, phase: e.phase, k: e.kForce });
        } else {
            cur = {
                i: e.i,
                j: e.j,
                k: e.k,
                l: e.l,
                terms: [{ n: e.periodicity, phase: e.phase, k: e.kForce }],
            };
            groups.push(cur);
        }
    }
    const nQuads = groups.length;
    const torsionIdx = new Int32Array(nQuads * 4);
    const torsionTermsPerTorsion = new Int32Array(nQuads);
    let totalTerms = 0;
    for (let q = 0; q < nQuads; q++) {
        const g = groups[q];
        torsionIdx[4 * q] = g.i;
        torsionIdx[4 * q + 1] = g.j;
        torsionIdx[4 * q + 2] = g.k;
        torsionIdx[4 * q + 3] = g.l;
        torsionTermsPerTorsion[q] = g.terms.length;
        totalTerms += g.terms.length;
    }
    const torsionK = new Float64Array(totalTerms);
    const torsionN = new Int32Array(totalTerms);
    const torsionPhase = new Float64Array(totalTerms);
    let t = 0;
    for (const g of groups) {
        for (const term of g.terms) {
            torsionK[t] = term.k;
            torsionN[t] = term.n;
            torsionPhase[t] = term.phase;
            t++;
        }
    }
    return {
        torsionIdx, torsionTermsPerTorsion, torsionK, torsionN, torsionPhase,
    };
}

/**
 * Derive the 1-4 scaled-pairs list. If the server schema evolves to ship
 * `scaledPairs` explicitly, use it directly. Otherwise, derive both 1-2/1-3
 * exclusions and 1-4 scaled pairs from the bond graph via
 * `topology/exclusions.js`. Ring topology (a pair classified as both 1-3
 * and 1-4 via different paths) is handled correctly there: 1-3 wins.
 *
 * @param {RawForcefieldData} rawFFD
 * @param {number} nAtoms
 * @param {Int32Array} bondIdx   Flat 2*nBonds (i,j) pairs from the projected topology.
 * @returns {Int32Array}
 */
function deriveScaledPairs(rawFFD, nAtoms, bondIdx) {
    if (rawFFD.scaledPairs instanceof Int32Array) return rawFFD.scaledPairs;
    const { scaledPairs } = buildExclusionSets(bondIdx, nAtoms);
    return scaledPairs;
}

function concatInt32(a, b) {
    const out = new Int32Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}
function concatFloat64(a, b) {
    const out = new Float64Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}
